/**
 * The bootstrap wizard's routes (CS r5b deliverable 164; flow G), owner 2.J, stage `s1`, since
 * `exit-2b`, frozen: three steps, each a preview that writes nothing and opens no batch, and an
 * apply of the same request with the preview's hash, one batch, marked `changeBatch` (a marked
 * preview would fail parity). A stale hash answers 409 `row_conflict`; a plan the rows refuse 422
 * `invalid_body`. The ledger step's targets are owner pointers (`deny`): a reference document is
 * never marked. NOW is `clock.advance` with `reason: 'bootstrap'`, not a route here.
 */
import { BootstrapAppliedV1, BootstrapLedgerApplyV1, BootstrapLedgerV1, BootstrapPreviewV1, BootstrapSessionsApplyV1, BootstrapSessionsV1, BootstrapStatusApplyV1, BootstrapStatusV1, } from '../dto/bootstrap.js';
import { defineRoute } from '../route/define-route.js';
import { EXAMPLE_BOOTSTRAP_APPLIED, EXAMPLE_BOOTSTRAP_LEDGER, EXAMPLE_BOOTSTRAP_LEDGER_PREVIEW, EXAMPLE_BOOTSTRAP_SESSIONS, EXAMPLE_BOOTSTRAP_SESSIONS_PREVIEW, EXAMPLE_BOOTSTRAP_STATUS, EXAMPLE_BOOTSTRAP_STATUS_PREVIEW, EXAMPLE_PREVIEW_HASH, NEEDS_PREVIEW_HASH, } from './examples-2b-batches.js';
const bootstrap = {
    auth: 'session',
    status: 'frozen',
    since: 'exit-2b',
    owner: '2.J',
    stage: 's1',
};
const TARGETS = ['body.targets[].documentId'];
const preview = { response: { kind: 'json', schema: BootstrapPreviewV1 } };
const applied = { response: { kind: 'json', schema: BootstrapAppliedV1 } };
const applyErrors = { 409: ['row_conflict'], 422: ['invalid_body'] };
const hash = { previewHash: EXAMPLE_PREVIEW_HASH };
/** Step 1's preview: per number, create, restore or skip. */
export const bootstrapSessionsPreview = defineRoute({
    id: 'bootstrap.sessions.preview',
    method: 'POST',
    path: '/api/bootstrap/sessions/preview',
    body: BootstrapSessionsV1,
    ...preview,
    errors: { 422: ['invalid_body'] },
    reference: 'none',
    ...bootstrap,
    examples: {
        request: { body: EXAMPLE_BOOTSTRAP_SESSIONS },
        response: EXAMPLE_BOOTSTRAP_SESSIONS_PREVIEW,
    },
});
/** Step 1's apply: the past sessions, `played`, origin `bootstrap`; one batch. */
export const bootstrapSessionsApply = defineRoute({
    id: 'bootstrap.sessions.apply',
    method: 'POST',
    path: '/api/bootstrap/sessions/apply',
    body: BootstrapSessionsApplyV1,
    ...applied,
    errors: applyErrors,
    reference: 'none',
    ...bootstrap,
    changeBatch: true,
    examples: {
        request: { body: { ...EXAMPLE_BOOTSTRAP_SESSIONS, ...hash } },
        response: EXAMPLE_BOOTSTRAP_APPLIED({ create: 1, skip: 1 }),
        consumes: true,
        success: NEEDS_PREVIEW_HASH,
    },
});
/** Step 2's preview: per document of the type, its status and play record from the map. */
export const bootstrapStatusPreview = defineRoute({
    id: 'bootstrap.status.preview',
    method: 'POST',
    path: '/api/bootstrap/status/preview',
    body: BootstrapStatusV1,
    ...preview,
    errors: { 422: ['invalid_body'] },
    reference: 'none',
    ...bootstrap,
    examples: {
        request: { body: EXAMPLE_BOOTSTRAP_STATUS },
        response: EXAMPLE_BOOTSTRAP_STATUS_PREVIEW,
    },
});
/** Step 2's apply: the canon statuses and before-tracking play records; one batch. */
export const bootstrapStatusApply = defineRoute({
    id: 'bootstrap.status.apply',
    method: 'POST',
    path: '/api/bootstrap/status/apply',
    body: BootstrapStatusApplyV1,
    ...applied,
    errors: applyErrors,
    reference: 'none',
    ...bootstrap,
    changeBatch: true,
    examples: {
        request: { body: { ...EXAMPLE_BOOTSTRAP_STATUS, ...hash } },
        response: EXAMPLE_BOOTSTRAP_APPLIED({ unchanged: 1 }),
        consumes: true,
        success: NEEDS_PREVIEW_HASH,
    },
});
/** Step 3's preview: per target, the assertion or play record, or unchanged. */
export const bootstrapLedgerPreview = defineRoute({
    id: 'bootstrap.ledger.preview',
    method: 'POST',
    path: '/api/bootstrap/ledger/preview',
    body: BootstrapLedgerV1,
    ...preview,
    errors: { 422: ['invalid_body'] },
    reference: 'deny',
    docIds: { owner: TARGETS },
    ...bootstrap,
    examples: {
        request: { body: EXAMPLE_BOOTSTRAP_LEDGER },
        response: EXAMPLE_BOOTSTRAP_LEDGER_PREVIEW,
    },
});
/** Step 3's apply: the known and played marks; one batch. */
export const bootstrapLedgerApply = defineRoute({
    id: 'bootstrap.ledger.apply',
    method: 'POST',
    path: '/api/bootstrap/ledger/apply',
    body: BootstrapLedgerApplyV1,
    ...applied,
    errors: applyErrors,
    reference: 'deny',
    docIds: { owner: TARGETS },
    ...bootstrap,
    changeBatch: true,
    examples: {
        request: { body: { ...EXAMPLE_BOOTSTRAP_LEDGER, ...hash } },
        response: EXAMPLE_BOOTSTRAP_APPLIED({ record: 2 }),
        consumes: true,
        success: NEEDS_PREVIEW_HASH,
    },
});
//# sourceMappingURL=bootstrap.js.map